import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AI_CHAT_CLOSE_TIMEOUT_MS, AI_CHAT_IDLE_TIMEOUT_MS } from '../core/constants/ai.const';
import { responseStatus } from '../core/constants/responseConst';
import { AiConvoIdleMessage } from '../models/ai.model';
import { aiService } from './ai.service';
import { LoggerService } from './logger.service';

export interface AiChatIdleHandlers {
  getChatId: () => string;
  isAgentMode: () => boolean;
  onFollowUp: (message: AiConvoIdleMessage) => void;
  onClosed: () => void;
}

/**
 * - `idle-wait`: AI reply shown, counting down to `/idle`.
 * - `close-wait`: follow-up shown, counting down to `/end`.
 * - `*-request`: the matching API call is in flight.
 */
type IdlePhase = 'off' | 'idle-wait' | 'idle-request' | 'close-wait' | 'end-request';

/**
 * Auto follow-up and auto-close for an idle AI chat.
 *
 * Root-scoped so a pending countdown survives the chat panel closing: timers
 * pause on `detach()` and the elapsed time is checked again on the next `attach()`.
 * Countdowns are wall-clock based so background tabs / suspended apps catch up
 * on `visibilitychange` instead of relying on throttled `setTimeout`.
 */
@Injectable({ providedIn: 'root' })
export class AiChatIdleService {
  private readonly _aiService = inject(aiService);
  private readonly logger = inject(LoggerService);
  private readonly document = inject(DOCUMENT);
  private readonly platformBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private handlers: AiChatIdleHandlers | null = null;
  private phase: IdlePhase = 'off';
  private chatId = '';
  /** Wall-clock start of the current countdown (AI reply or follow-up shown). */
  private phaseStartedAt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** Chat taken over by a human agent — no idle handling for it. */
  private disabledChatId = '';
  /** Chat already closed via `/end` — never call it again. */
  private endedChatId = '';

  private readonly onVisibilityChange = (): void => {
    if (this.document.visibilityState === 'hidden') {
      this.clearTimer();
    } else {
      this.evaluate();
    }
  };

  /**
   * Binds the chat screen. A countdown paused by an earlier `detach()` that has
   * since expired ends that chat first, so the caller's `/convo/start` gets a
   * fresh one. Otherwise reopening the chat counts as user activity.
   */
  async attach(handlers: AiChatIdleHandlers): Promise<void> {
    if (!this.platformBrowser) {
      return;
    }

    this.handlers = handlers;
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (this.isPausedCountdownExpired()) {
      await this.endChat(false);
      return;
    }

    if (this.phase === 'idle-wait' || this.phase === 'close-wait') {
      this.phase = 'off';
    }
  }

  /** Unbinds the chat screen. Timers stop; the countdown is kept for `attach()`. */
  detach(): void {
    this.clearTimer();
    this.handlers = null;
    if (this.platformBrowser) {
      this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  /** Call after each AI reply is shown. */
  start(): void {
    if (!this.handlers || this.phase === 'idle-request' || this.phase === 'end-request') {
      return;
    }

    const chatId = this.handlers.getChatId();
    if (
      !chatId ||
      chatId === this.disabledChatId ||
      chatId === this.endedChatId ||
      this.handlers.isAgentMode()
    ) {
      this.stop();
      return;
    }

    this.chatId = chatId;
    this.enterCountdown('idle-wait');
  }

  /** Call on any user activity. The next countdown starts after the next AI reply. */
  reset(): void {
    if (this.phase === 'end-request') {
      return;
    }

    // An in-flight `/idle` response is dropped once the phase moves off `idle-request`.
    this.stop();
  }

  /** Call when the chat switches to a human agent. */
  stop(): void {
    this.clearTimer();
    if (this.phase !== 'end-request') {
      this.phase = 'off';
    }
  }

  private enterCountdown(phase: 'idle-wait' | 'close-wait'): void {
    this.phase = phase;
    this.phaseStartedAt = Date.now();
    this.evaluate();
  }

  private evaluate(): void {
    this.clearTimer();

    if (!this.handlers || (this.phase !== 'idle-wait' && this.phase !== 'close-wait')) {
      return;
    }

    if (this.handlers.isAgentMode() || this.handlers.getChatId() !== this.chatId) {
      this.stop();
      return;
    }

    const elapsed = Date.now() - this.phaseStartedAt;

    if (this.phase === 'idle-wait') {
      if (elapsed >= AI_CHAT_IDLE_TIMEOUT_MS + AI_CHAT_CLOSE_TIMEOUT_MS) {
        void this.endChat(true);
      } else if (elapsed >= AI_CHAT_IDLE_TIMEOUT_MS) {
        void this.requestFollowUp();
      } else {
        this.schedule(AI_CHAT_IDLE_TIMEOUT_MS - elapsed);
      }
      return;
    }

    if (elapsed >= AI_CHAT_CLOSE_TIMEOUT_MS) {
      void this.endChat(true);
    } else {
      this.schedule(AI_CHAT_CLOSE_TIMEOUT_MS - elapsed);
    }
  }

  private schedule(delayMs: number): void {
    if (this.document.visibilityState === 'hidden') {
      return;
    }
    this.timer = setTimeout(() => this.evaluate(), delayMs);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private isPausedCountdownExpired(): boolean {
    const elapsed = Date.now() - this.phaseStartedAt;
    if (this.phase === 'idle-wait') {
      return elapsed >= AI_CHAT_IDLE_TIMEOUT_MS + AI_CHAT_CLOSE_TIMEOUT_MS;
    }
    if (this.phase === 'close-wait') {
      return elapsed >= AI_CHAT_CLOSE_TIMEOUT_MS;
    }
    return false;
  }

  private async requestFollowUp(): Promise<void> {
    const chatId = this.chatId;
    this.phase = 'idle-request';

    try {
      const res = await firstValueFrom(this._aiService.idleConversationToAi(chatId));
      if (this.phase !== 'idle-request' || this.chatId !== chatId) {
        return;
      }

      if (res.statusCode === responseStatus.BADREQUEST) {
        this.markChatClosed(chatId, true);
        return;
      }

      if (res.statusCode !== responseStatus.SUCCESS) {
        this.phase = 'off';
        return;
      }

      const message = res.data?.message;
      if (!message) {
        this.disabledChatId = chatId;
        this.phase = 'off';
        return;
      }

      this.enterCountdown('close-wait');
      this.handlers?.onFollowUp(message);
    } catch (error) {
      if (this.phase !== 'idle-request' || this.chatId !== chatId) {
        return;
      }

      // 400 = chat already closed/resolved on the server.
      if (error instanceof HttpErrorResponse && error.status === responseStatus.BADREQUEST) {
        this.markChatClosed(chatId, true);
        return;
      }

      this.logger.warn('[AI Chat] Idle follow-up failed', error);
      this.phase = 'off';
    }
  }

  private async endChat(notifyClosed: boolean): Promise<void> {
    const chatId = this.chatId;
    this.clearTimer();

    if (!chatId || chatId === this.endedChatId) {
      this.phase = 'off';
      return;
    }

    this.phase = 'end-request';
    let closed = true;

    try {
      const res = await firstValueFrom(this._aiService.endConversationToAi(chatId));
      closed = res.statusCode !== responseStatus.SUCCESS || res.data?.closed !== false;
    } catch (error) {
      // Clear locally anyway — the next `/convo/start` reconciles with the server.
      this.logger.warn('[AI Chat] Idle auto-close failed', error);
    }

    if (!closed) {
      this.disabledChatId = chatId;
      this.phase = 'off';
      return;
    }

    this.markChatClosed(chatId, notifyClosed);
  }

  private markChatClosed(chatId: string, notifyClosed: boolean): void {
    this.endedChatId = chatId;
    this.chatId = '';
    this.phase = 'off';
    this.clearTimer();

    if (notifyClosed) {
      this.handlers?.onClosed();
    }
  }
}
